import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import OpenPlayToolsLayout from '../../components/openplay/OpenPlayToolsLayout';
import Icon from '../../components/openplay/OpenPlayIcon';
import { resolveBoardConfig } from './communityConfig';
import { supabase } from '../../lib/supabaseClient';

const PAGE_SIZE = 15;

const CommunityListPage = () => {
  const params = useParams();
  const navigate = useNavigate();
  const boardKey = String(params?.board || 'matching');
  const config = resolveBoardConfig(boardKey);

  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedItems = useMemo(() => items, [items]);

  const loadItems = async () => {
    setLoading(true);
    setLoadError('');
    try {
      let query = supabase
        .from('community_posts')
        .select('id, board_key, title, author_name, image_urls, view_count, created_at, updated_at, neighborhood_name', { count: 'exact' })
        .eq('board_key', boardKey)
        .order('created_at', { ascending: false });

      const keyword = search.trim();
      if (keyword) {
        const pattern = `%${keyword}%`;
        query = query.or(`title.ilike.${pattern},content.ilike.${pattern},author_name.ilike.${pattern}`);
      }

      const from = (currentPage - 1) * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;
      const { data, error, count } = await query.range(from, to);
      if (error) throw error;
      setItems(data || []);
      setTotalCount(count || 0);
    } catch (err) {
      setLoadError(err?.message || '게시글을 불러오지 못했습니다.');
      setItems([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, [boardKey, search, currentPage]);

  const handleWrite = async () => {
    const { data } = await supabase.auth.getSession();
    if (!data?.session?.user) {
      navigate('/openplay/user-authentication');
      return;
    }
    navigate(`/openplay/community/${boardKey}/new`);
  };

  return (
    <OpenPlayToolsLayout userRole="player">
      <main className="pt-6 md:pt-10 pb-[calc(84px+env(safe-area-inset-bottom,16px))] md:pb-10">
        <div className="max-w-5xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="mb-6 md:mb-8">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
              <div>
                <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-2">
                  {config.label}
                </h1>
                <p className="text-base md:text-lg caption text-muted-foreground">{config.description}</p>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="relative">
                  <Icon name="MagnifyingGlassIcon" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    value={search}
                    onChange={(event) => {
                      setSearch(event.target.value);
                      setPage(1);
                    }}
                    placeholder="검색어를 입력하세요"
                    className="h-10 w-64 rounded-lg border border-border bg-background pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleWrite}
                  className="h-10 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-semibold flex items-center justify-center"
                >
                  글쓰기
                </button>
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="grid grid-cols-[64px_minmax(0,1fr)_120px_90px_80px] gap-2 px-4 py-3 text-xs font-semibold text-muted-foreground border-b border-border">
              <span>No</span>
              <span>제목</span>
              <span>작성자</span>
              <span>조회</span>
              <span>상태</span>
            </div>
            {loading ? (
              <div className="px-4 py-10 text-center text-sm text-muted-foreground">불러오는 중...</div>
            ) : loadError ? (
              <div className="px-4 py-10 text-center text-sm text-destructive">{loadError}</div>
            ) : pagedItems.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-muted-foreground">등록된 게시글이 없습니다.</div>
            ) : (
              <ul className="divide-y divide-border">
                {pagedItems.map((item, index) => (
                  <li key={item.id} className="grid grid-cols-[64px_minmax(0,1fr)_120px_90px_80px] gap-2 px-4 py-3 text-sm">
                    <span className="text-muted-foreground">{(currentPage - 1) * PAGE_SIZE + index + 1}</span>
                    <Link
                      to={`/openplay/community/${boardKey}/${item.id}`}
                      className="text-left font-medium text-foreground hover:text-primary transition-smooth"
                    >
                      {(boardKey === 'matching' || boardKey === 'free') && item.neighborhood_name
                        ? `[${item.neighborhood_name}] `
                        : ''}
                      {item.title}
                    </Link>
                    <span className="text-muted-foreground">{item.author_name}</span>
                    <span className="text-muted-foreground">{item.view_count || 0}</span>
                    <span className="text-xs text-muted-foreground">{item.updated_at ? '수정됨' : '신규'}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex items-center justify-between mt-4">
            <span className="text-xs text-muted-foreground">총 {totalCount}건</span>
            <div className="flex items-center gap-2">
              {Array.from({ length: totalPages }).map((_, idx) => {
                const pageNum = idx + 1;
                return (
                  <button
                    key={pageNum}
                    type="button"
                    onClick={() => setPage(pageNum)}
                    className={`h-8 w-8 rounded-md text-sm font-medium transition-smooth ${
                      pageNum === currentPage
                        ? 'bg-primary text-primary-foreground'
                        : 'border border-border text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </main>
    </OpenPlayToolsLayout>
  );
};

export default CommunityListPage;
